import $ from 'jquery'
import _ from 'lodash'
import Immutable from 'immutable'
import excelExport from 'scoreboard/components/export/excel-export'
import cleanObject from 'scoreboard/components/clean-object'
import i18n from 'scoreboard/components/i18n'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import Dataset from '../model/dataset'
const { TOTAL, BLANK  } = Dataset
import { OTHER } from './datasets-tabular-result'
import DatasetField from '../model/dataset-field'
import unwrapModel from 'scoreboard/components/unwrap-model'
import wrapModel from 'scoreboard/components/wrap-model'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import datasetFieldTimeFormat from '../dataset-field-time-format'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, TEXT, DATE } = DataType

const ISO_DATE_REGEX = /^(\d{4})\D?(0[1-9]|1[0-2])\D?([12]\d|0[1-9]|3[01])$/

const datasetsOverviewExport = {

	/**
	 * Exports all the tables on the datasets overview
	 *
	 * @param {JQuery[]} tableEls
	 *
	 * @param {Backbone.Model} model Datasets Overview Model
	 *
	 * @param {Object[]} exportModels one model per table
	 * @param {String} exportModels.field.dataType
	 * @param {String|Number|Immutable.map} exportModels.data The raw data for the table cells
	 * @param {Backbone.Model} [exportModels.rowBreakdownField] If a row breakdown is set
	 * @param {Object[]} [exportModels.rowArray] The row values for a tabular result view
	 * @param {Function} [exportModels.otherDatum] The row values for the other row
	 */
	export: function (tableEls, model, exportModels) {
		// Scan DOM for exportable tables
		const tables = []
		const tableLabels = {}
		tableEls.each((i,el) => {
			const panel = $(el)
			// #59579 multiple tables could have the same fieldId, so don't
			// use that for an ID. The Backbone cid is unique
			const id = panel.data('table-cid')
			tables.push({id, bodyEl: panel.find('tbody')})
			tableLabels[id] = panel.find('h3').text()
		})

		// Provide labels and a way to lookup raw model data
		var opts = {
			fileLabel: model.get('label'),
			label: tableLabels,
			dataAttributes: {
				modelLookup: this._modelLookup.bind(this, model, exportModels)
			}
		}

		// One table per sheet coming right up
		return excelExport.saveTablesAsSheetsInFile(tables, opts)
	},

	_modelLookup (model, exportModels, tableId, tableRowIndex, jqCell) {
		if (jqCell.is('th')) {
			return {}
		}

		const exportModel = exportModels[tableId]
		const dataAttributes = jqCell.data()
		// field and rowBreakdownField are Backbone.Model
		const {field, rowBreakdownField, rowArray, data, table = {}} = exportModel
		const {row, column, fieldId, aggregationType, aggregationIndex, rowIndex} = dataAttributes

		const unwrappedField = unwrapModel(field)
		const {dataType} = unwrappedField
		// value-result assigned by default
		const cellModel = {
			rowId: row,
			value: data.result,
			dataType,
			timeFormat: datasetFieldTimeFormat(field)
		}

		// tabular-result cell
		if (!data.result) {
			const colIndex = jqCell.index()
			const isRowBreakdown = !_.isNil(rowIndex)
			const colIndexOffset = table.showAncestors ? 1 : 0
			const isTableFieldColumn = colIndex === colIndexOffset && !isRowBreakdown

			const rowEl = jqCell.parent()
			if (isTableFieldColumn || isRowBreakdown) {
				const id = isTableFieldColumn ? rowEl.data('id') : rowArray[rowIndex]
				const f = isTableFieldColumn ? field : rowBreakdownField
				cellModel.dataType = this._getDataTypeForField(f, id, exportModel)
				cellModel.value = this._getValueForFieldColumn(model, f, id, exportModel)
			} else {
				let value = jqCell.data('value')
				const {valueType} = rowEl.data()
				if (valueType === 'text') {
					value += '' // #17152
				}
				let datum
				if (value === OTHER) {
					datum = exportModel.otherDatum()
				} else {
					const key = Immutable.fromJS(cleanObject({value,row,column}))
					datum = data.get(key)
				}
				const cellField = model.combinedFieldsById[fieldId]
				this._readModelProperties(cellModel, cellField, datum, aggregationIndex)
			}
			if (isTableFieldColumn) {
				const level = jqCell.data('level') || (jqCell.hasClass('indent') ? 1 : 0)
				const padding = _.padStart('', 4 * level, ' ')
				cellModel.value = cellModel.value && padding + cellModel.value
			}
		}
		this._inferModelProperties(cellModel, aggregationType, fieldId)

		if (jqCell.find('.blank-value, .show-more').length > 0) {
			// #49599 override dataset field's datatype for the "blank" and
			// "other" rows to prevent formatting problems
			cellModel.dataType = TEXT
		}

		return cellModel
	},

	_getDataTypeForField(field, id, exportModel) {
		const unwrappedField = unwrapModel(field)
		let {dataType} = unwrappedField
		if (this._isRangeItem(field, id) || _.get(exportModel, `treeItemsById[${id}]`)) {
			dataType = TEXT
		}
		return dataType
	},

	/**
	 * All the rules for how to derive raw values for the table field cells
	 * #17140, #17208, #29213
	 *
	 * @param {Dataset} model
	 * @param {Object} field unwrapped field object
	 */
	_getValueForFieldColumn(model, field, rowId, exportModel) {
		const notRangeItem = !this._isRangeItem(field, rowId)
		let value

		// #41043 - Don't consider tree items if we're dealing with a range,
		// must use the text in the table (the range label).
		// Even if a field is numeric a range makes it effectively text
		if (notRangeItem) {

			// If there are tree items the rowId will map to a label
			if (isGeographicOrRollup(model.getUnderlyingField(field).dataType)) {
				value = _.get(exportModel, `treeItemsById[${rowId}].label`)
			}

			// Otherwise, use the raw numeric value from the rowId if type is numeric
			// And not a textual aggregation (e.g. range)
			if (!value &&
				rowId !== BLANK && // #29213 Just use the DOM value for this
				isNumericDataType(unwrapModel(field).dataType)) {
				value = rowId
			}
		}

		return value
	},

	/**
	 * It's not enough to check if ranges exist.  The row breakdown can be set
	 * on the value OR the range.  So the row id must be a range id to use the
	 * range value for export formatting.
	 */
	_isRangeItem(field, id) {
		const f = wrapModel(field, DatasetField)
		return f.ranges && f.ranges.get(id)
	},

	/**
	 * Applies raw dataset value and dataType to export model object for a given table cell
	 *
	 * @param cellModel
	 * @param cellField
	 * @param datum
	 * @param aggregationIndex
	 * @private
	 */
	_readModelProperties(cellModel, cellField, datum, aggregationIndex) {
		const unwrappedField = unwrapModel(cellField)
		cellModel.dataType = _.get(unwrappedField, 'dataType', cellModel.dataType)
		if (datum !== undefined) {
			cellModel.value = datum[aggregationIndex]
		}
	},

	/**
	 * Inspects the aggregation type and fieldId to assign
	 * the correct dataTypes and default nil values
	 *
	 * @param {Object} cellModel
	 * @param {String} aggregationType
	 * @param {Number} fieldId
	 * @private
	 */
	_inferModelProperties(cellModel, aggregationType, fieldId) {

		if (aggregationType === 'percentage') {
			cellModel.dataType = 'percent'
		} else if (aggregationType === 'unique') {
			cellModel.dataType = 'number'
		} else if (fieldId !== undefined && fieldId == TOTAL) { //coercion required
			cellModel.dataType = NUMBER
		}

		if (_.isNil(cellModel.value) && (
				aggregationType === 'sum' ||
				aggregationType === 'percentage'
			)) {
			cellModel.value = cellModel.dataType === DATE ? null : 0
		}

		// #16868 dates that are aggregations must be formatted as text
		if (cellModel.dataType === DATE && !_.invoke(cellModel, 'value.match', ISO_DATE_REGEX)) {
			cellModel.dataType = TEXT
		}

		if (cellModel.value === OTHER) {
			cellModel.value = i18n.get('common.other')
		}
	}
}

export default datasetsOverviewExport
datasetsOverviewExport.__filename__ = 'datasets/overview/datasets-overview-export.js'
